import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  Users,
  Mail,
  Download,
  Share2,
  CheckCircle2,
  Timer,
  Ticket,
  Info,
  Phone,
  CreditCard,
  Building2,
} from "lucide-react";
import { toast } from "react-toastify";
import Footer from "../../components/landing/Footer";

const STATUS_CONFIG = {
  pending: {
    label: "Payment Pending",
    icon: Timer,
    color: "amber",
    bgClass: "bg-amber-50",
    textClass: "text-amber-700",
    borderClass: "border-amber-300",
    iconClass: "text-amber-600",
  },
  approved: {
    label: "Paid & Confirmed",
    icon: CheckCircle2,
    color: "green",
    bgClass: "bg-green-50",
    textClass: "text-green-700",
    borderClass: "border-green-300",
    iconClass: "text-green-600",
  },
};

export default function ConferenceRegistrationDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    window.scrollTo(0,0);
  },[]);  

  useEffect(() => {
    // Get registration data from location state
    if (location.state?.registration) {
      setRegistration(location.state.registration);
      setLoading(false);
    } else {
      // If no data in state, redirect back
      toast.error("Registration details not found");
      navigate("/conference-registrations");
    }
  }, [location.state, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return "Date TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Date TBA";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const getEventDateInfo = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return {
        month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
        day: date.getDate(),
        year: date.getFullYear(),
        weekday: date.toLocaleDateString("en-US", { weekday: "long" }),
      };
    } catch {
      return null;
    }
  };

  const handleDownloadTicket = () => {
    toast.info("Download feature coming soon");
  };

  const handleShareTicket = () => {
    toast.info("Share feature coming soon");
  };

  if (loading || !registration) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen">
        <div className="relative mb-6">
          <div className="w-20 h-20 rounded-full border-4 border-orange-200 animate-spin border-t-orange-500" />
          <div className="flex absolute inset-0 justify-center items-center">
            <Ticket size={32} className="text-orange-500" />
          </div>
        </div>
        <p className="text-lg font-semibold text-gray-600">Loading registration details...</p>
      </div>
    );
  }

  const eventData = registration.event || {};
  const organizerData = eventData.organizer || {};
  const paymentStatus = (registration.payment_status || registration.status || "pending").toLowerCase();
  const status =
    registration.status ||
    (paymentStatus === "pending" ? "pending" : paymentStatus === "paid" || paymentStatus === "verified" || paymentStatus === "approved" ? "approved" : "pending");
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  // Treat null/undefined registration_type as single (API often returns null for single)
  const isGroup = registration.registration_type === "group" || !!registration.group_info;
  const registrationTypeLabel = isGroup ? "Group" : "Single";
  const isPending = status === "pending" || !status;

  const handleCompletePayment = () => {
    const slug = registration.event?.slug;
    if (!slug) return;
    const eventDataForPay = registration.event || {};
    const isGroupPay = registration.registration_type === "group" || !!registration.group_info;
    const numberOfAttendees =
      (isGroupPay && (registration.group_info?.number_of_attendees ?? registration.number_of_attendees)) ||
      (Array.isArray(registration.attendees) && registration.attendees.length) ||
      undefined;
    const ticketPrice =
      registration.ticket_type != null ? parseFloat(registration.ticket_type.price) : NaN;
    const computedTotal =
      numberOfAttendees != null && !Number.isNaN(ticketPrice)
        ? Number((numberOfAttendees * ticketPrice).toFixed(2))
        : undefined;
    const totalAmount =
      registration.total_amount != null
        ? parseFloat(registration.total_amount)
        : registration.amount != null
          ? parseFloat(registration.amount)
          : computedTotal;
    const ticketTypeName = registration.ticket_type?.name;
    const ticketTypePrice =
      registration.ticket_type != null ? parseFloat(registration.ticket_type.price) : NaN;
    const selectedTicket =
      eventDataForPay?.ticket_types?.length && (ticketTypeName != null || !Number.isNaN(ticketTypePrice))
        ? eventDataForPay.ticket_types.find((t) => t.name === ticketTypeName) ??
          eventDataForPay.ticket_types.find((t) => Math.abs(parseFloat(t.price) - ticketTypePrice) < 0.02) ??
          eventDataForPay.ticket_types[0]
        : registration.ticket_type ?? undefined;

    navigate(`/event-details/${slug}/payment`, {
      state: {
        registrationData: {
          ...registration,
          id: registration.id,
          registration_id: registration.id,
          slug: registration.slug || registration.application_id || registration.id,
        },
        eventData: eventDataForPay,
        slug,
        payLaterEntry: true,
        fromRegistrations: true,
        isGroup: isGroupPay,
        numberOfAttendees,
        totalAmount,
        attendees: registration.attendees ?? undefined,
        selectedTicket,
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-20 mt-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 py-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
          <motion.button
            onClick={() => navigate("/conference-registrations")}
            whileHover={{ x: -4, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex gap-2 items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl border border-gray-200 transition-all hover:bg-gray-200 hover:shadow-md"
          >
            <ChevronLeft size={18} />
            Back
          </motion.button>
        </div>
      </div>

      <div className="px-4 py-6 mx-auto max-w-8xl sm:px-6 lg:px-8 md:py-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          {/* Main Content */}
          <div className="space-y-6 lg:col-span-2">
            {/* Event Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm md:p-6"
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex-1">
                  <h1 className="mb-3 text-xl font-bold text-gray-900 md:text-2xl">
                    {eventData.event_title || "Event Details"}
                  </h1>
                  
                  {/* Status Badge */}
                  <div className="inline-flex">
                    <div
                      className={`flex gap-2 items-center px-4 py-2 text-sm font-bold rounded-full border-2 ${statusConfig.bgClass} ${statusConfig.textClass} ${statusConfig.borderClass}`}
                    >
                      <StatusIcon size={16} />
                      <span>{statusConfig.label}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons - same logic as list card: Pay when pending, Download/Share when paid */}
                <div className="flex gap-2">
                  {!isPending && (
                    <motion.button
                      onClick={handleShareTicket}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex gap-2 items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl border border-gray-200 transition-all hover:bg-gray-200"
                    >
                      <Share2 size={16} />
                      <span className="hidden sm:inline">Share</span>
                    </motion.button>
                  )}
                  {isPending ? (
                    <motion.button
                      onClick={handleCompletePayment}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-md transition-all hover:from-orange-600 hover:to-orange-700 hover:shadow-lg"
                    >
                      <CreditCard size={16} />
                      <span className="hidden sm:inline">Complete payment</span>
                      <span className="sm:hidden">Pay</span>
                    </motion.button>
                  ) : (
                    <motion.button
                      onClick={handleDownloadTicket}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-md transition-all hover:from-orange-600 hover:to-orange-700 hover:shadow-lg"
                    >
                      <Download size={16} />
                      <span className="hidden sm:inline">Download</span>
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Event Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm md:p-6"
            >
              <h2 className="mb-4 text-lg font-bold text-gray-900">Event Details</h2>
              
              <div className="space-y-4">
                {/* Date & Time */}
                {((eventData.event_start_date || eventData.event_date) || (eventData.event_end_date || eventData.event_date)) && (
                  <div className="flex gap-3 items-start">
                    <div className="flex-shrink-0 p-2 bg-emerald-100 rounded-lg">
                      <Calendar size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        Start: {formatDate(eventData.event_start_date || eventData.event_date)}
                      </p>
                      {(eventData.event_end_date || eventData.event_date) && (eventData.event_end_date || eventData.event_date) !== (eventData.event_start_date || eventData.event_date) && (
                        <p className="text-sm font-semibold text-gray-900">
                          End: {formatDate(eventData.event_end_date || eventData.event_date)}
                        </p>
                      )}
                      {(eventData.event_start_time || eventData.event_time || eventData.event_end_time) && (
                        <p className="text-sm text-gray-600">
                          {eventData.event_start_time && eventData.event_end_time && eventData.event_start_time !== eventData.event_end_time
                            ? `${formatTime(eventData.event_start_time)} – ${formatTime(eventData.event_end_time)}`
                            : formatTime(eventData.event_start_time || eventData.event_time)}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Venue */}
                {eventData.venue_name && (
                  <div className="flex gap-3 items-start">
                    <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                      <MapPin size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {eventData.venue_name}
                      </p>
                      {eventData.venue_address && (
                        <p className="text-sm text-gray-600">
                          {eventData.venue_address}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Registration Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm md:p-6"
            >
              <div className="flex flex-wrap gap-2 items-center mb-4">
                {isGroup ? <Users size={20} className="text-gray-600" /> : <User size={20} className="text-gray-600" />}
                <h2 className="text-lg font-bold text-gray-900">
                  {isGroup ? "Group Registration" : "Your Registration"}
                </h2>
                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${registrationTypeLabel === "Group" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-700"}`}>
                  Registration type: {registrationTypeLabel}
                </span>
              </div>

              <div className="space-y-4">
                {/* Application / Registration ID */}
                {(registration.application_id || registration.slug) && (
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-white rounded-lg border border-orange-200">
                    <p className="mb-1 text-xs font-semibold tracking-wider text-gray-600 uppercase">
                      Application ID
                    </p>
                    <p className="font-mono text-lg font-bold text-orange-600">
                      {registration.application_id || registration.slug}
                    </p>
                  </div>
                )}

                {isGroup ? (
                  <>
                    {/* Group info */}
                    {registration.group_info && (
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex gap-2 items-center mb-3">
                          <Building2 size={18} className="text-orange-600" />
                          <p className="text-sm font-bold text-gray-900 uppercase tracking-wide">Group details</p>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {registration.group_info.organization_name && (
                            <div>
                              <p className="mb-0.5 text-xs font-semibold text-gray-500 uppercase">Organization</p>
                              <p className="text-sm font-medium text-gray-900">{registration.group_info.organization_name}</p>
                            </div>
                          )}
                          {registration.group_info.country && (
                            <div>
                              <p className="mb-0.5 text-xs font-semibold text-gray-500 uppercase">Country</p>
                              <p className="text-sm font-medium text-gray-900">{registration.group_info.country}</p>
                            </div>
                          )}
                          {registration.group_info.city && (
                            <div>
                              <p className="mb-0.5 text-xs font-semibold text-gray-500 uppercase">City</p>
                              <p className="text-sm font-medium text-gray-900">{registration.group_info.city}</p>
                            </div>
                          )}
                          {(registration.group_info.number_of_attendees != null || registration.number_of_attendees != null) && (
                            <div>
                              <p className="mb-0.5 text-xs font-semibold text-gray-500 uppercase">Number of attendees</p>
                              <p className="text-sm font-medium text-gray-900">
                                {registration.group_info.number_of_attendees ?? registration.number_of_attendees}
                              </p>
                            </div>
                          )}
                          {registration.group_info.primary_contact_name && (
                            <div className="sm:col-span-2">
                              <p className="mb-0.5 text-xs font-semibold text-gray-500 uppercase">Primary contact</p>
                              <p className="text-sm font-medium text-gray-900">{registration.group_info.primary_contact_name}</p>
                              {registration.group_info.primary_contact_email && (
                                <p className="text-xs text-gray-600">{registration.group_info.primary_contact_email}</p>
                              )}
                              {registration.group_info.primary_contact_phone && (
                                <p className="text-xs text-gray-600">{registration.group_info.primary_contact_phone}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Primary contact details (motivation answers) */}
                    {registration.primary_contact_details?.motivation_answers?.length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="mb-2 text-xs font-bold text-gray-500 uppercase tracking-wide">Primary contact answers</p>
                        <ul className="space-y-2">
                          {registration.primary_contact_details.motivation_answers.map((a, i) => (
                            <li key={i} className="text-sm">
                              <span className="font-medium text-gray-700">{a.question}</span>
                              <span className="text-gray-600">: {a.answer}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Attendees */}
                    {Array.isArray(registration.attendees) && registration.attendees.length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="mb-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Attendees</p>
                        <ul className="space-y-3">
                          {registration.attendees.map((att, i) => (
                            <li key={i} className="flex gap-3 items-start p-3 bg-white rounded-lg border border-gray-100">
                              <div className="flex justify-center items-center w-8 h-8 rounded-full bg-orange-100 text-orange-700 text-sm font-bold shrink-0">
                                {i + 1}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900">
                                  {att.first_name} {att.last_name}
                                </p>
                                {att.email && <p className="text-xs text-gray-600">{att.email}</p>}
                                {att.organization && <p className="text-xs text-gray-500">{att.organization}</p>}
                                {att.contact_number && <p className="text-xs text-gray-500">{att.contact_number}</p>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Ticket type & amount */}
                    {(registration.ticket_type || registration.total_amount != null) && (
                      <div className="p-4 bg-gradient-to-br from-orange-50 to-white rounded-lg border border-orange-200">
                        {registration.ticket_type && (
                          <div className="mb-3">
                            <p className="mb-0.5 text-xs font-semibold text-gray-500 uppercase">Ticket type</p>
                            <p className="text-sm font-bold text-gray-900">{registration.ticket_type.name}</p>
                            <p className="text-xs text-gray-600">
                              {registration.ticket_type.price === 0 || parseFloat(registration.ticket_type.price) === 0
                                ? "FREE"
                                : `GH₵ ${registration.ticket_type.price} per ticket`}
                            </p>
                          </div>
                        )}
                        {(() => {
                          const num = registration.group_info?.number_of_attendees ?? registration.number_of_attendees ?? registration.attendees?.length;
                          const total =
                            registration.total_amount != null
                              ? parseFloat(registration.total_amount)
                              : registration.amount != null
                                ? parseFloat(registration.amount)
                                : num != null && registration.ticket_type != null
                                  ? Number((num * parseFloat(registration.ticket_type.price)).toFixed(2))
                                  : null;
                          if (total == null && registration.amount_paid == null) return null;
                          const displayAmount = total ?? registration.amount_paid;
                          return (
                            <div className="flex justify-between items-center pt-2 border-t border-orange-200">
                              <span className="text-sm font-semibold text-gray-700">
                                {registration.amount_paid != null ? "Amount paid" : "Total amount"}
                              </span>
                              <span className="text-lg font-bold text-gray-900">
                                {parseFloat(displayAmount) === 0 ? "FREE" : `GH₵ ${Number(displayAmount).toFixed(2)}`}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Single registrant details */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {registration.first_name && (
                        <div>
                          <p className="mb-1 text-xs font-semibold text-gray-600 uppercase">First Name</p>
                          <p className="text-sm font-medium text-gray-900">{registration.first_name}</p>
                        </div>
                      )}
                      {registration.last_name && (
                        <div>
                          <p className="mb-1 text-xs font-semibold text-gray-600 uppercase">Last Name</p>
                          <p className="text-sm font-medium text-gray-900">{registration.last_name}</p>
                        </div>
                      )}
                      {registration.email && (
                        <div>
                          <p className="mb-1 text-xs font-semibold text-gray-600 uppercase">Email</p>
                          <p className="text-sm font-medium text-gray-900">{registration.email}</p>
                        </div>
                      )}
                      {(registration.phone || registration.contact_number) && (
                        <div>
                          <p className="mb-1 text-xs font-semibold text-gray-600 uppercase">Phone</p>
                          <p className="text-sm font-medium text-gray-900">{registration.phone || registration.contact_number}</p>
                        </div>
                      )}
                      {registration.company_name && (
                        <div className="md:col-span-2">
                          <p className="mb-1 text-xs font-semibold text-gray-600 uppercase">Company</p>
                          <p className="text-sm font-medium text-gray-900">{registration.company_name}</p>
                        </div>
                      )}
                    </div>

                    {/* Ticket type for single */}
                    {registration.ticket_type && (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="mb-0.5 text-xs font-semibold text-gray-500 uppercase">Ticket type</p>
                        <p className="text-sm font-bold text-gray-900">{registration.ticket_type.name}</p>
                      </div>
                    )}

                    {/* Amount Paid */}
                    {(registration.amount_paid !== undefined && registration.amount_paid !== null) && (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-gray-600">Amount Paid</span>
                          <span className="text-lg font-bold text-gray-900">
                            {parseFloat(registration.amount_paid) === 0 ? "FREE" : `GH₵ ${parseFloat(registration.amount_paid).toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>

            {/* Organizer Information */}
            {organizerData && organizerData.name && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm md:p-6"
              >
                <h2 className="mb-4 text-lg font-bold text-gray-900">Event Organizer</h2>
                
                <div className="flex gap-4 items-start">
                  {/* Organizer Logo */}
                  {organizerData.logo && (
                    <div className="flex-shrink-0">
                      <img
                        src={organizerData.logo}
                        alt={organizerData.name}
                        className="object-cover w-16 h-16 rounded-lg border border-gray-200"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Organizer Details */}
                  <div className="flex-1 space-y-3">
                    <div>
                      <h3 className="text-base font-bold text-gray-900">
                        {organizerData.name}
                      </h3>
                      {organizerData.city && organizerData.country && (
                        <p className="text-sm text-gray-600">
                          {organizerData.city}, {organizerData.country}
                        </p>
                      )}
                    </div>

                    {/* Contact Information */}
                    <div className="mt-4 space-y-3">
                      <a
                        href="mailto:info@tripsecuregh.com?subject=Inquiry about Event"
                        className="flex gap-3 items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg shadow-sm transition-all hover:bg-emerald-700 hover:shadow-md"
                      >
                        <Mail size={16} />
                        <span>Email Event Team</span>
                      </a>
                      
                      <a
                        href="tel:+233204014370"
                        className="flex gap-3 items-center justify-center px-4 py-2.5 text-sm font-medium text-emerald-700 bg-white rounded-lg border-2 border-emerald-600 shadow-sm transition-all hover:bg-emerald-50"
                      >
                        <Phone size={16} />
                        <span>Call Event Team</span>
                      </a>
                      
                      <div className="pt-3 mt-3 space-y-2 text-xs text-gray-600 border-t border-gray-100">
                        <div className="flex gap-2 items-center">
                          <Mail size={14} className="text-gray-400" />
                          <span>info@tripsecuregh.com</span>
                        </div>
                        <div className="flex gap-2 items-center">
                          <Phone size={14} className="text-gray-400" />
                          <span>+233 (0)20 401 4370</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Terms & Conditions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm md:p-6"
            >
              <h2 className="mb-4 text-lg font-bold text-gray-900">Terms & Conditions</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="mb-2 text-sm font-bold text-gray-900">
                    Organizer's Terms & Conditions
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-600">
                    This ticket is proof of purchase of the above event and must be presented on entry. 
                    This ticket is non-transferable and only one ticket is valid per ticket purchased. 
                    Tickets are not to be duplicated or falsified for entry. Refunds are available at 
                    the discretion of the Event Organizer.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Event Image */}
            {eventData.thumbnail && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="overflow-hidden rounded-xl border border-gray-200 shadow-sm"
              >
                <img
                  src={eventData.thumbnail}
                  alt={eventData.event_title}
                  className="object-cover w-full h-64"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </motion.div>
            )}

            {/* Quick Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-4 bg-gradient-to-br from-orange-50 to-white rounded-xl border-2 border-orange-200 shadow-sm"
            >
              <div className="flex gap-2 items-center mb-3">
                <Info size={20} className="text-orange-600" />
                <h3 className="text-sm font-bold text-gray-900">Important Information</h3>
              </div>
              
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex gap-2 items-start">
                  <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5 text-orange-600" />
                  <span>Please bring a valid ID to the event</span>
                </li>
                <li className="flex gap-2 items-start">
                  <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5 text-orange-600" />
                  <span>Arrive 30 minutes before start time</span>
                </li>
                <li className="flex gap-2 items-start">
                  <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5 text-orange-600" />
                  <span>Keep this confirmation handy</span>
                </li>
              </ul>
            </motion.div>

            {/* Event Date Calendar */}
            {eventData.event_date && (() => {
              const dateInfo = getEventDateInfo(eventData.event_date);
              return dateInfo ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm"
                >
                  {/* Calendar Header */}
                  <div className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600">
                    <p className="text-xs font-bold tracking-wider text-center text-white uppercase">
                      {dateInfo.month}
                    </p>
                  </div>
                  
                  {/* Calendar Body */}
                  <div className="p-4 text-center">
                    <p className="text-4xl font-extrabold leading-none text-gray-900">
                      {dateInfo.day}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-600">
                      {dateInfo.year}
                    </p>
                    <div className="pt-3 mt-3 border-t border-gray-200">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        {dateInfo.weekday}
                      </p>
                      {eventData.event_time && (
                        <div className="flex gap-1 justify-center items-center mt-2">
                          <Clock size={14} className="text-orange-600" />
                          <p className="text-sm font-bold text-orange-600">
                            {formatTime(eventData.event_time)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ) : null;
            })()}

            {/* Need Help */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
            >
              <h3 className="mb-3 text-sm font-bold text-gray-900">Need Help?</h3>
              <p className="mb-4 text-sm text-gray-600">
                Contact the TripSecure Event Team for any questions about this event.
              </p>
              
              <div className="space-y-2">
                <a
                  href="mailto:info@tripsecuregh.com?subject=Inquiry about Event"
                  className="flex gap-2 justify-center items-center px-4 py-2 w-full text-sm font-semibold text-orange-600 bg-orange-50 rounded-lg border border-orange-200 transition-all hover:bg-orange-100"
                >
                  <Mail size={16} />
                  Email Event Team
                </a>
                
                <a
                  href="tel:+233204014370"
                  className="flex gap-2 justify-center items-center px-4 py-2 w-full text-sm font-semibold text-orange-600 bg-white rounded-lg border border-orange-200 transition-all hover:bg-orange-50"
                >
                  <Phone size={16} />
                  Call Event Team
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

